import { useState, useEffect } from 'react'
import { expenseAPI } from '../services/api'
import { formatCurrency, formatDate, getCategoryIcon, getCategoryLabel, CATEGORIES, exportToCSV } from '../utils/helpers'
import ExpenseForm from '../components/ExpenseForm'

export default function Expenses() {
  const [expenses, setExpenses] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState('month')
  const [filterCategory, setFilterCategory] = useState('all')
  const [showForm, setShowForm] = useState(false)
  const [editExpense, setEditExpense] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const periods = [
    { value: 'today', label: 'Hari Ini' },
    { value: 'week', label: 'Minggu Ini' },
    { value: 'month', label: 'Bulan Ini' },
    { value: 'year', label: 'Tahun Ini' },
  ]

  useEffect(() => {
    fetchData()
  }, [period, filterCategory, page])

  const fetchData = async () => {
    setLoading(true)
    try {
      const params = {
        period,
        page,
        limit: 20,
      }
      if (filterCategory !== 'all') params.category = filterCategory

      const [expensesData, summaryData] = await Promise.all([
        expenseAPI.getAll(params),
        expenseAPI.getSummary({ period }),
      ])

      setExpenses(expensesData.expenses || [])
      setTotalPages(expensesData.totalPages || 1)
      setSummary(summaryData)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id) => {
    try {
      await expenseAPI.delete(id)
      setDeleteConfirm(null)
      fetchData()
    } catch (err) {
      console.error(err)
    }
  }

  const handleExport = async () => {
    try {
      const data = await expenseAPI.getAll({ period, limit: 999 })
      const csvData = data.expenses.map(e => ({
        Tanggal: formatDate(e.date),
        Kategori: getCategoryLabel(e.category),
        'Jumlah (Rp)': e.amount,
        Catatan: e.description || '',
      }))
      exportToCSV(csvData, `pengeluaran-${period}.csv`)
    } catch (err) {
      console.error(err)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Pengeluaran</h2>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="p-2 text-gray-500 hover:text-primary transition-colors"
            title="Export CSV"
          >
            📥
          </button>
          <button
            onClick={() => { setEditExpense(null); setShowForm(true) }}
            className="btn-primary text-sm"
          >
            + Tambah
          </button>
        </div>
      </div>

      {/* Period Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {periods.map(p => (
          <button
            key={p.value}
            onClick={() => { setPeriod(p.value); setPage(1) }}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              period === p.value
                ? 'bg-primary text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Summary */}
      {summary && (
        <div className="card">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-gray-500 text-xs">Total Pengeluaran</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(summary.total || 0)}</p>
            </div>
            <div>
              <p className="text-gray-500 text-xs">Jumlah Transaksi</p>
              <p className="text-xl font-bold text-gray-900">{summary.count || 0}</p>
            </div>
          </div>

          {/* Category Breakdown */}
          {summary.byCategory && summary.byCategory.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 mb-3">Per Kategori</p>
              <div className="space-y-2">
                {summary.byCategory.slice(0, 4).map(cat => (
                  <div key={cat.category} className="flex items-center gap-2">
                    <span className="text-sm">{getCategoryIcon(cat.category)}</span>
                    <div className="flex-1">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">{getCategoryLabel(cat.category)}</span>
                        <span className="font-medium">{formatCurrency(cat.total)}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div
                          className="h-1.5 bg-primary rounded-full"
                          style={{ width: `${(cat.total / summary.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <button
          onClick={() => { setFilterCategory('all'); setPage(1) }}
          className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
            filterCategory === 'all'
              ? 'bg-gray-900 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100'
          }`}
        >
          Semua
        </button>
        {CATEGORIES.map(cat => (
          <button
            key={cat.value}
            onClick={() => { setFilterCategory(cat.value); setPage(1) }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1 ${
              filterCategory === cat.value
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            {cat.icon} {cat.label.split(' ')[0]}
          </button>
        ))}
      </div>

      {/* Expense List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="card animate-pulse">
              <div className="flex gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-xl" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : expenses.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-4xl mb-2">📭</p>
          <p className="text-gray-500">Belum ada pengeluaran</p>
        </div>
      ) : (
        <div className="space-y-3">
          {expenses.map((expense) => (
            <div key={expense.id} className="card flex items-center gap-4 py-3">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                {getCategoryIcon(expense.category)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {expense.description || getCategoryLabel(expense.category)}
                </p>
                <p className="text-xs text-gray-500">
                  {getCategoryLabel(expense.category)} • {formatDate(expense.date)}
                </p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <p className="font-semibold text-gray-900">{formatCurrency(expense.amount)}</p>
                <div className="flex gap-1">
                  <button
                    onClick={() => { setEditExpense(expense); setShowForm(true) }}
                    className="p-1.5 text-gray-400 hover:text-primary transition-colors"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(expense)}
                    className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-secondary disabled:opacity-50"
          >
            ← Sebelumnya
          </button>
          <span className="flex items-center px-4 text-sm text-gray-600">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-secondary disabled:opacity-50"
          >
            Berikutnya →
          </button>
        </div>
      )}

      {/* Expense Form Modal */}
      {showForm && (
        <ExpenseForm
          expense={editExpense}
          onClose={() => { setShowForm(false); setEditExpense(null) }}
          onSuccess={() => { setShowForm(false); setEditExpense(null); fetchData() }}
        />
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-lg mb-2">Hapus Pengeluaran?</h3>
            <p className="text-gray-500 text-sm mb-1">
              {deleteConfirm.description || getCategoryLabel(deleteConfirm.category)}
            </p>
            <p className="font-semibold text-gray-900 mb-6">{formatCurrency(deleteConfirm.amount)}</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1">
                Batal
              </button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="btn-danger flex-1">
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
